import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MentionIndex, Message } from "@playbookai/player-ai-client";
import type {
  PlayerAIMentionNavigation,
  PlayerAIMessageReference,
} from "@playbookai/ui";
import { mentionsUser } from "./rail-message-context.js";

type VisibleRange = {
  readonly first: number | null;
  readonly last: number | null;
};

/**
 * Tasks 6 and 7 — taking the reader to a message anywhere in the
 * conversation: the viewer's mentions (↑ ↓) and the message a preview points
 * at. A target outside the loaded history is loaded first, then handed to the
 * rail as a focus request, which scrolls to it and highlights it briefly.
 *
 * The mention count is a TOTAL, never an unread count: the server's index
 * covers the whole history, and messages that arrived since it was read are
 * added from the transcript. Reading a mention changes nothing.
 */
export function useMessageNavigation({
  conversationId,
  currentUserId,
  mentionsEnabled,
  messages,
  loadMentionIndex,
  loadMessagesThrough,
}: {
  readonly conversationId: string | null;
  readonly currentUserId: string | null;
  readonly mentionsEnabled: boolean;
  readonly messages: readonly Message[];
  readonly loadMentionIndex: () => Promise<MentionIndex | null>;
  readonly loadMessagesThrough: (sequence: number) => Promise<boolean>;
}): {
  readonly mentionNavigation: PlayerAIMentionNavigation | undefined;
  readonly focusRequest:
    | { readonly id: string; readonly messageId: string }
    | undefined;
  readonly requestMessage: (reference: PlayerAIMessageReference) => void;
} {
  const [index, setIndex] = useState<{
    readonly conversationId: string;
    readonly index: MentionIndex;
  } | null>(null);
  useEffect(() => {
    if (!mentionsEnabled || !conversationId) return;
    let live = true;
    void loadMentionIndex().then((loaded) => {
      if (live && loaded) setIndex({ conversationId, index: loaded });
    });
    return () => {
      live = false;
    };
  }, [conversationId, loadMentionIndex, mentionsEnabled]);

  const mentions = useMemo(() => {
    if (!mentionsEnabled || !conversationId || !currentUserId) return null;
    const indexed =
      index?.conversationId === conversationId ? index.index : null;
    const indexedSequences = indexed?.message_sequences ?? [];
    const newestIndexed = indexedSequences.at(-1) ?? 0;
    const loaded = messages
      .filter((message) => mentionsUser(message, currentUserId))
      .map((message) => message.sequence);
    const known = new Set(indexedSequences);
    const since = loaded.filter(
      (sequence) => sequence > newestIndexed && !known.has(sequence),
    );
    const sequences = [...new Set([...indexedSequences, ...loaded])].sort(
      (left, right) => left - right,
    );
    return {
      sequences,
      count: Math.max((indexed?.count ?? 0) + since.length, sequences.length),
    };
  }, [conversationId, currentUserId, index, mentionsEnabled, messages]);

  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [pendingSequence, setPendingSequence] = useState<number | null>(null);
  const [focusRequest, setFocusRequest] = useState<
    { readonly id: string; readonly messageId: string } | undefined
  >(undefined);
  const focusCounterRef = useRef(0);
  const cursorRef = useRef<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    cursorRef.current = null;
    setPendingSequence(null);
    setFocusRequest(undefined);
  }, [conversationId]);

  // A requested message becomes a focus request as soon as it is loaded.
  useEffect(() => {
    if (pendingSequence === null) return;
    const target = messages.find(
      (message) => message.sequence === pendingSequence,
    );
    if (!target) return;
    setPendingSequence(null);
    focusCounterRef.current += 1;
    setFocusRequest({
      id: `focus-${focusCounterRef.current}`,
      messageId: target.id,
    });
  }, [messages, pendingSequence]);

  const goTo = useCallback(
    async (sequence: number): Promise<void> => {
      setPendingSequence(sequence);
      const loaded = messagesRef.current.some(
        (message) => message.sequence === sequence,
      );
      if (loaded) return;
      setBusy(true);
      try {
        if (!(await loadMessagesThrough(sequence))) {
          setPendingSequence((current) =>
            current === sequence ? null : current,
          );
        }
      } finally {
        setBusy(false);
      }
    },
    [loadMessagesThrough],
  );

  const sequencesRef = useRef<readonly number[]>([]);
  sequencesRef.current = mentions?.sequences ?? [];
  const onNavigate = useCallback(
    (direction: "previous" | "next", from: VisibleRange): void => {
      const sequences = sequencesRef.current;
      const cursor = cursorRef.current;
      // Step from the last mention visited while it is still on screen;
      // otherwise start from where the reader is, counting what they see.
      const current =
        cursor !== null &&
        from.first !== null &&
        from.last !== null &&
        cursor >= from.first &&
        cursor <= from.last
          ? cursor
          : null;
      const target =
        direction === "previous"
          ? [...sequences]
              .reverse()
              .find((sequence) =>
                current !== null
                  ? sequence < current
                  : sequence <= (from.last ?? Number.POSITIVE_INFINITY),
              )
          : sequences.find((sequence) =>
              current !== null
                ? sequence > current
                : sequence >= (from.first ?? Number.NEGATIVE_INFINITY),
            );
      if (target === undefined) return;
      cursorRef.current = target;
      void goTo(target);
    },
    [goTo],
  );

  const requestMessage = useCallback(
    (reference: PlayerAIMessageReference): void => {
      if (reference.sequence !== null) void goTo(reference.sequence);
    },
    [goTo],
  );

  return {
    mentionNavigation:
      mentions && mentions.count > 0
        ? { count: mentions.count, onNavigate, busy }
        : undefined,
    focusRequest,
    requestMessage,
  };
}
